import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Inbox, Search as SearchIcon, Filter, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth, canWrite } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const INQUIRY_STATUS_FALLBACK = [
  "New Inquiry", "Awaiting Information", "Rights Review", "Legal Review Required",
  "Quote Preparation", "Negotiation", "Declined", "Closed", "Licensed",
];

const STATUS_COLOUR = {
  "New Inquiry":            { bg: "hsl(220 80% 92%)", fg: "hsl(220 80% 28%)" },
  "Awaiting Information":   { bg: "hsl(40 70% 92%)",  fg: "hsl(38 70% 28%)" },
  "Rights Review":          { bg: "hsl(20 30% 90%)",  fg: "hsl(20 30% 28%)" },
  "Legal Review Required":  { bg: "hsl(0 60% 92%)",   fg: "hsl(0 60% 30%)" },
  "Quote Preparation":      { bg: "hsl(280 40% 92%)", fg: "hsl(280 40% 28%)" },
  "Negotiation":            { bg: "hsl(140 38% 90%)", fg: "hsl(140 50% 22%)" },
  "Declined":               { bg: "hsl(0 30% 92%)",   fg: "hsl(0 30% 28%)" },
  "Closed":                 { bg: "hsl(225 14% 92%)", fg: "hsl(225 14% 28%)" },
  "Licensed":               { bg: "hsl(347 50% 90%)", fg: "hsl(347 70% 22%)" },
};

function StatusBadge({ status }) {
  const s = STATUS_COLOUR[status] || STATUS_COLOUR["New Inquiry"];
  return (
    <Badge
      variant="outline"
      style={{ background: s.bg, color: s.fg, borderColor: s.fg + "33" }}
      className="text-[10px] uppercase tracking-wide font-medium whitespace-nowrap"
      data-testid="sync-inquiry-status-badge"
    >
      {status}
    </Badge>
  );
}

function fmtDate(iso) {
  if (!iso) return "\u2014";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso; }
}

export default function SyncOpportunities() {
  const { user } = useAuth();
  const writer = canWrite(user);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [statuses, setStatuses] = useState(INQUIRY_STATUS_FALLBACK);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (statusFilter && statusFilter !== "all") params.status = statusFilter;
      const r = await api.get("/gfmp/inquiries", { params });
      setItems(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(errMsg(e, "Could not load Sync Opportunities."));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => {
    if (!writer) return;
    refresh();
    api.get("/gfmp/meta")
      .then((r) => {
        if (Array.isArray(r.data?.inquiry_statuses)) setStatuses(r.data.inquiry_statuses);
      })
      .catch(() => {});
  }, [writer, refresh]);

  function submitSearch(e) {
    e?.preventDefault?.();
    setQ(searchInput.trim());
  }

  const summary = useMemo(() => {
    const counts = { total: items.length };
    statuses.forEach((s) => (counts[s] = 0));
    items.forEach((it) => {
      counts[it.status] = (counts[it.status] || 0) + 1;
    });
    return counts;
  }, [items, statuses]);

  if (!writer) {
    return (
      <div className="max-w-2xl mx-auto" data-testid="sync-opportunities-readonly">
        <Alert>
          <AlertDescription>
            Sync Opportunities are visible to Catalog Manager, GFMP Reviewer, and Super Admin only.
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sync-opportunities-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground mb-1">
            GFMP · Internal
          </div>
          <h1 className="font-display text-3xl md:text-4xl tracking-tight flex items-center gap-3">
            <Inbox className="w-7 h-7" style={{ color: "hsl(347 60% 22%)" }} />
            Sync Opportunities
          </h1>
          <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
            Inquiries submitted through the public GFMP Sync Catalogue land here as private records.
            <span className="font-medium text-foreground"> No licence, quote, or rights representation may be issued
            until rights authority and contract status are confirmed.</span>
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={refresh} data-testid="sync-opportunities-refresh">
          <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick counts</CardTitle>
          <CardDescription>Across all sync opportunities currently in scope.</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="sync-opportunities-summary">
          {["total", ...statuses].slice(0, 10).map((s) => (
            <div key={s} className="rounded-md border p-2.5">
              <div className="text-[9px] uppercase tracking-[0.18em] text-muted-foreground">{s === "total" ? "Total" : s}</div>
              <div className="font-display text-xl" style={{ color: "hsl(347 60% 22%)" }} data-testid={`sync-opportunities-count-${s.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{summary[s] ?? 0}</div>
            </div>
          ))}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-4 space-y-4">
          <form onSubmit={submitSearch} className="flex flex-wrap items-end gap-3" data-testid="sync-opportunities-filter-form">
            <div className="flex-1 min-w-[220px]">
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Search (name, company, email, project)
              </label>
              <div className="flex items-center gap-2">
                <SearchIcon className="w-3.5 h-3.5 text-muted-foreground" />
                <Input
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search\u2026"
                  data-testid="sync-opportunities-search"
                />
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">
                Status
              </label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[220px]" data-testid="sync-opportunities-status-filter">
                  <Filter className="w-3.5 h-3.5 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {statuses.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" size="sm" data-testid="sync-opportunities-apply">
              Apply
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => { setSearchInput(""); setQ(""); setStatusFilter("all"); }}
              data-testid="sync-opportunities-clear"
            >
              Clear
            </Button>
          </form>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          <Table data-testid="sync-opportunities-table">
            <TableHeader>
              <TableRow>
                <TableHead className="w-[160px]">Received</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Project</TableHead>
                <TableHead className="w-[140px]">Type</TableHead>
                <TableHead className="w-[170px]">Status</TableHead>
                <TableHead className="w-[80px] text-right">Open</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8">Loading\u2026</TableCell>
                </TableRow>
              )}
              {!loading && items.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground py-8" data-testid="sync-opportunities-empty">
                    No sync opportunities match the current filters.
                  </TableCell>
                </TableRow>
              )}
              {!loading && items.map((it) => (
                <TableRow key={it.id} data-testid={`sync-opportunity-row-${it.id}`}>
                  <TableCell className="text-[12px]">
                    <div>{fmtDate(it.created_at)}</div>
                    <div className="font-mono text-[10px] text-muted-foreground">{it.id?.slice(0, 8)}</div>
                  </TableCell>
                  <TableCell className="text-[12.5px]">
                    <div className="font-medium">{it.contact_name || "\u2014"}</div>
                    <div className="text-muted-foreground text-[11px] truncate max-w-[260px]" title={it.email}>
                      {it.email}{it.company ? ` · ${it.company}` : ""}
                    </div>
                  </TableCell>
                  <TableCell className="text-[12.5px]">
                    <div className="font-medium">{it.project_title || "(no title)"}</div>
                    {it.tracks_of_interest_titles?.length > 0 && (
                      <div className="text-[11px] text-muted-foreground truncate max-w-[280px]">
                        Interested in: {it.tracks_of_interest_titles.join(", ")}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-[12px]">{it.project_type || "\u2014"}</TableCell>
                  <TableCell><StatusBadge status={it.status} /></TableCell>
                  <TableCell className="text-right">
                    <Button asChild size="sm" variant="ghost" data-testid={`sync-opportunity-open-${it.id}`}>
                      <Link to={`/sync-opportunities/${it.id}`}>Open</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
